import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState } from 'react-native';
import {
  Alert,
  Linking,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from '../components/rn';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ActivityIndicator from '../components/LoadingIndicator';
import PageHeader from '../components/PageHeader';
import { screenHeaderContentInset } from '../components/screenHeaderLayout';
import { useTranslation } from '../i18n/I18nContext';
import { uppercaseForDisplayLanguage } from '../utils/locale';
import {
  getPushPermissionState,
  PushPermissionState,
  requestPushPermission,
} from '../notifications/pushLifecycle';
import {
  DEFAULT_NOTIFICATION_TOPIC_PREFERENCES,
  MESSAGE_PREVIEW_OPTIONS,
  NOTIFICATION_SECTIONS,
  NOTIFICATION_TOPICS,
  NotificationPreferencePatch,
  NotificationPreferences,
  NotificationSection,
  NotificationTopic,
} from '../notifications/preferences';
import { api } from '../services/api';
import { fontSize, spacing, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { MIN_TOUCH_TARGET } from '../components/CompactActionButton';
import CompactActionButton from '../components/CompactActionButton';
import { InfoButton, InfoText, useInfoSet } from '../components/InfoHint';

type Props = {
  token: string;
  onBack: () => void;
};

export default function NotificationSettingsScreen({ token, onBack }: Props) {
  const colors = useTheme();
  const { t, lang } = useTranslation();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [activeSection, setActiveSection] = useState<NotificationSection | null>(null);
  const [permission, setPermission] = useState<PushPermissionState>('unsupported');
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // 7.14 / D-654 — açıklamalar ⓘ arkasında; ekran her açıldığında kapalı.
  const info = useInfoSet();

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    Promise.all([
      api.getNotificationPreferences(token),
      getPushPermissionState(),
    ])
      .then(([nextPreferences, nextPermission]) => {
        setPreferences({
          ...nextPreferences,
          topics: { ...DEFAULT_NOTIFICATION_TOPIC_PREFERENCES, ...(nextPreferences.topics ?? {}) },
        });
        setPermission(nextPermission);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') getPushPermissionState().then(setPermission);
    });
    return () => subscription.remove();
  }, []);

  async function updatePreference(patch: NotificationPreferencePatch) {
    if (!preferences || saving) return;
    const previous = preferences;
    setPreferences({
      ...preferences,
      ...patch,
      topics: patch.topics ? { ...preferences.topics, ...patch.topics } : preferences.topics,
    });
    setSaving(true);
    try {
      const saved = await api.updateNotificationPreferences(token, patch);
      setPreferences({
        ...saved,
        topics: { ...DEFAULT_NOTIFICATION_TOPIC_PREFERENCES, ...(saved.topics ?? {}) },
      });
    } catch {
      setPreferences(previous);
      Alert.alert(t('common.error'), t('profile.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function manageDevicePermission() {
    if (permission === 'granted' || permission === 'denied') {
      Linking.openSettings().catch(() => Alert.alert(t('common.error')));
      return;
    }
    setPermission(await requestPushPermission(token));
  }

  // Başlık içeriğin üstünde yüzüyor (7.14 · D-550): iki kaydırma alanı da aynı
  // yüksekliği tepede geri vermek zorunda. Bu ekranda İKİ ScrollView var —
  // kök liste ve bölüm listesi — ve biri unutulursa yalnız o ekranda ilk satır
  // başlığın altında kalır.
  const ustBosluk = screenHeaderContentInset(insets.top);

  const permissionLabel = permission === 'granted'
    ? t('notifications.stateOn')
    : permission === 'denied'
      ? t('notifications.stateOff')
      : permission === 'undetermined'
        ? t('notifications.stateAsk')
        : t('notifications.stateUnsupported');

  const selectedSection = activeSection
    ? NOTIFICATION_SECTIONS.find((section) => section.id === activeSection) ?? null
    : null;

  function updateTopic(topic: NotificationTopic, value: boolean) {
    updatePreference({ topics: { [topic]: value } });
  }

  function renderRootContent() {
    if (!preferences) return null;
    return (
      <ScrollView
        contentContainerStyle={[styles.content, { paddingTop: ustBosluk }]}
        scrollIndicatorInsets={{ top: ustBosluk }}>
        <View style={styles.section}>
          <View style={styles.settingRow}>
            <View style={styles.settingCopy}>
              <View style={styles.titleRow}>
                <Text style={styles.settingTitle}>{t('notifications.master')}</Text>
                <InfoButton open={info.isOpen('master')} onPress={() => info.toggle('master')} subject={t('notifications.master')} testID="notifications-master-info" />
              </View>
              <InfoText open={info.isOpen('master')} text={t('notifications.masterInfo')} />
            </View>
            <Switch
              value={preferences.push_enabled}
              accessibilityLabel={t('notifications.master')}
              onValueChange={(value) => {
                updatePreference({ push_enabled: value });
                // 9.26 / D-637 — bildirimler artık varsayılan olarak kapalı ve
                // gelen kutusu cihaz iznini istemiyor. Kişi burada açtığında izin
                // hiç sorulmamışsa ŞİMDİ sorulmalı; sorulmazsa hesapta açık,
                // telefonda kapalı kalır ve yine hiçbir şey gelmez.
                if (value && permission === 'undetermined') {
                  requestPushPermission(token).then(setPermission).catch(() => {});
                }
              }}
              disabled={saving}
              trackColor={{ false: colors.gray200, true: colors.black }}
              thumbColor={colors.white}
            />
          </View>
        </View>

        <Text style={styles.sectionLabel}>
          {uppercaseForDisplayLanguage(t('notifications.categories'), lang)}
        </Text>
        <View style={styles.section}>
          {NOTIFICATION_SECTIONS.map((section) => (
            <View style={styles.settingRow} key={section.id}>
              <TouchableOpacity
                style={styles.categoryLink}
                onPress={() => setActiveSection(section.id)}
                accessibilityRole="button"
                accessibilityLabel={t(section.labelKey)}>
                <View style={styles.settingCopy}>
                  <View style={styles.titleRow}>
                    <Text style={styles.settingTitle}>{t(section.labelKey)}</Text>
                    <InfoButton open={info.isOpen(section.id)} onPress={() => info.toggle(section.id)} subject={t(section.labelKey)} />
                  </View>
                  <InfoText open={info.isOpen(section.id)} text={t(section.infoKey)} />
                </View>
                <Text style={styles.disclosure}>›</Text>
              </TouchableOpacity>
              <Switch
                value={preferences[section.field]}
                accessibilityLabel={t(section.labelKey)}
                onValueChange={(value) => updatePreference({ [section.field]: value })}
                disabled={saving}
                trackColor={{ false: colors.gray200, true: colors.black }}
                thumbColor={colors.white}
              />
            </View>
          ))}
          <View style={styles.settingRow}>
            <View style={styles.settingCopy}>
              <View style={styles.titleRow}>
                <Text style={styles.settingTitle}>{t('notifications.reactions')}</Text>
                <InfoButton open={info.isOpen('reactions')} onPress={() => info.toggle('reactions')} subject={t('notifications.reactions')} />
              </View>
              <InfoText open={info.isOpen('reactions')} text={t('notifications.reactionsInfo')} />
            </View>
            <Switch
              value={preferences.reactions_enabled}
              accessibilityLabel={t('notifications.reactions')}
              onValueChange={(value) => updatePreference({ reactions_enabled: value })}
              disabled={saving}
              trackColor={{ false: colors.gray200, true: colors.black }}
              thumbColor={colors.white}
            />
          </View>
        </View>

        <Text style={styles.sectionLabel}>
          {uppercaseForDisplayLanguage(t('notifications.device'), lang)}
        </Text>
        <View style={styles.section}>
          <TouchableOpacity
            style={styles.settingRow}
            onPress={manageDevicePermission}
            disabled={permission === 'unsupported'}>
            <View style={styles.settingCopy}>
              <View style={styles.titleRow}>
                <Text style={styles.settingTitle}>{permissionLabel}</Text>
                <InfoButton open={info.isOpen('device')} onPress={() => info.toggle('device')} subject={permissionLabel} />
              </View>
              <InfoText open={info.isOpen('device')} text={t('notifications.deviceInfo')} />
            </View>
            {permission !== 'unsupported' && (
              <Text style={styles.chevron}>
                {permission === 'undetermined' ? t('notifications.enable') : t('notifications.openSettings')}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    );
  }

  function renderSectionContent() {
    if (!preferences || !selectedSection || !activeSection) return null;
    const parentEnabled = preferences[selectedSection.field];
    const topics = activeSection === 'messages' ? [] : NOTIFICATION_TOPICS[activeSection];
    return (
      <ScrollView
        contentContainerStyle={[styles.content, { paddingTop: ustBosluk }]}
        scrollIndicatorInsets={{ top: ustBosluk }}>
        <View style={styles.section}>
          <View style={styles.settingRow}>
            <View style={styles.settingCopy}>
              <View style={styles.titleRow}>
                <Text style={styles.settingTitle}>{t('notifications.categoryEnabled')}</Text>
                <InfoButton open={info.isOpen('category')} onPress={() => info.toggle('category')} subject={t('notifications.categoryEnabled')} />
              </View>
              <InfoText open={info.isOpen('category')} text={t('notifications.categoryEnabledInfo')} />
            </View>
            <Switch
              value={parentEnabled}
              accessibilityLabel={`${t(selectedSection.labelKey)}: ${t('notifications.categoryEnabled')}`}
              onValueChange={(value) => updatePreference({ [selectedSection.field]: value })}
              disabled={saving}
              trackColor={{ false: colors.gray200, true: colors.black }}
              thumbColor={colors.white}
            />
          </View>
        </View>

        {topics.length > 0 && (
          <>
            <Text style={styles.sectionLabel}>
              {uppercaseForDisplayLanguage(t('notifications.events'), lang)}
            </Text>
            <View style={styles.section}>
              {topics.map((topic) => (
                <View style={[styles.settingRow, !parentEnabled && styles.disabledRow]} key={topic.field}>
                  <View style={styles.settingCopy}>
                    <View style={styles.titleRow}>
                      <Text style={styles.settingTitle}>{t(topic.labelKey)}</Text>
                      <InfoButton open={info.isOpen(topic.field)} onPress={() => info.toggle(topic.field)} subject={t(topic.labelKey)} />
                    </View>
                    <InfoText open={info.isOpen(topic.field)} text={t(topic.infoKey)} />
                  </View>
                  <Switch
                    value={preferences.topics[topic.field]}
                    accessibilityLabel={t(topic.labelKey)}
                    onValueChange={(value) => updateTopic(topic.field, value)}
                    disabled={saving || !parentEnabled}
                    trackColor={{ false: colors.gray200, true: colors.black }}
                    thumbColor={colors.white}
                  />
                </View>
              ))}
            </View>
          </>
        )}

        {activeSection === 'messages' && (
          <>
            <Text style={styles.sectionLabel}>
              {uppercaseForDisplayLanguage(t('notifications.preview'), lang)}
            </Text>
            <View style={styles.section}>
              {MESSAGE_PREVIEW_OPTIONS.map((option) => {
                const selected = preferences.message_preview === option.value;
                return (
                  <TouchableOpacity
                    key={option.value}
                    style={[styles.settingRow, !parentEnabled && styles.disabledRow]}
                    disabled={saving || !parentEnabled}
                    onPress={() => updatePreference({ message_preview: option.value })}
                    accessibilityRole="radio"
                    accessibilityState={{ selected, disabled: !parentEnabled }}>
                    <View style={styles.settingCopy}>
                      <View style={styles.titleRow}>
                        <Text style={styles.settingTitle}>{t(option.labelKey)}</Text>
                        <InfoButton open={info.isOpen(option.value)} onPress={() => info.toggle(option.value)} subject={t(option.labelKey)} />
                      </View>
                      <InfoText open={info.isOpen(option.value)} text={t(option.infoKey)} />
                    </View>
                    <View style={[styles.radio, selected && styles.radioSelected]}>
                      {selected && <View style={styles.radioDot} />}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        )}
      </ScrollView>
    );
  }

  return (
    <View style={styles.container}>
      <PageHeader
        title={selectedSection ? t(selectedSection.labelKey) : t('notifications.title')}
        onBack={() => activeSection ? setActiveSection(null) : onBack()}
        floating
      />

      {loading ? (
        <View style={styles.center}><ActivityIndicator color={colors.black} /></View>
      ) : failed || !preferences ? (
        <View style={styles.center}>
          <Text style={styles.errorText}>{t('common.error')}</Text>
          <CompactActionButton
              variant="primary"
              label={t('common.retry')}
              onPress={load}
              style={styles.retryButton}
              testID="notification-settings-retry"
            />
        </View>
      ) : activeSection ? renderSectionContent() : renderRootContent()}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.white },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.lg },
  errorText: { color: colors.gray600, marginBottom: spacing.md },
  retryButton: { backgroundColor: colors.black, borderRadius: radius.control, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  retryText: { color: colors.white, fontWeight: '600' },
  content: { paddingHorizontal: spacing.md, paddingBottom: 80 },
  sectionLabel: {
    color: colors.gray400,
    fontSize: fontSize.sm,
    fontWeight: '600',
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    marginHorizontal: spacing.xs,
  },
  section: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.gray200 },
  settingRow: {
    minHeight: 72,
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.gray200,
  },
  settingCopy: { flex: 1 },
  disabledRow: { opacity: 0.45 },
  settingTitle: { color: colors.black, fontSize: fontSize.md, fontWeight: '500', flexShrink: 1 },
  // 7.14 / D-654 — başlık ve ⓘ aynı satırda.
  titleRow: { flexDirection: 'row', alignItems: 'center' },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 2,
    borderColor: colors.gray400,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioSelected: { borderColor: colors.black },
  radioDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: colors.black },
  // D-412 — ayar listesi satırı: görsel kutu büyüyebildiği için hedefe
  // BÜYÜTEREK çıkılır, kullanıcı nereye basacağını görür.
  categoryLink: { flex: 1, minHeight: MIN_TOUCH_TARGET, flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
  disclosure: { color: colors.gray400, fontSize: 28, lineHeight: 30 },
  chevron: { color: colors.black, fontSize: fontSize.sm, fontWeight: '600', maxWidth: 110, textAlign: 'right' },
});
